import { useEffect, useRef, useState } from 'react'
import { Camera, ImageMinus, Laptop, Loader2, LogOut, ShieldCheck, Trash2, X } from 'lucide-react'

import { PortfolioCarousel } from '@/components/portfolio-carousel'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Checkbox, Input, Select, Textarea } from '@/components/ui/field'
import { Dialog } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import type { User } from '@/lib/types'
import {
  useChangePassword,
  useDeviceSessions,
  useRemoveAvatar,
  useRevokeSession,
  useSendVerification,
  useSession,
  useUploadAvatar,
} from '@/features/auth/api'
import {
  useAddEducation,
  useAddExperience,
  useAddPortfolio,
  useDeleteTalentItem,
  useExpert,
  useSaveExpertProfile,
  useRemovePortfolioImage,
  useSaveTalentDetails,
  useTalentProfile,
  useUploadPortfolioImage,
} from '@/features/marketplace/api'
import { employmentLabel } from '@/components/ui/status'
import { ApiError } from '@/lib/api'
import { formatDateShort, formatDateTime, formatRelative } from '@/lib/format'

const roleLabels: Record<string, string> = {
  student: 'Peserta',
  expert: 'Tenaga Ahli',
  teaching_assistant: 'Asisten Pengajar',
  client: 'Klien',
  employer: 'Perekrut',
  admin_ops: 'Admin Ops',
  mediator: 'Mediator',
  finance: 'Finance',
  super_admin: 'Super Admin',
}

/**
 * AksiKarya menyediakan penggantian foto dan penghapusan satu karya.
 *
 * Foto diunggah per karya, bukan sekaligus, karena setiap foto memang milik
 * satu karya tertentu — dan mengunggah sekumpulan foto tanpa judul hanya
 * menghasilkan galeri yang tidak menjelaskan apa pun.
 */
function AksiKarya({ item, onHapus }: { item: { id: string; image_url?: string }; onHapus: () => void }) {
  const berkas = useRef<HTMLInputElement>(null)
  const unggah = useUploadPortfolioImage()
  const hapusFoto = useRemovePortfolioImage()
  const toast = useToast()

  const sibuk = unggah.isPending || hapusFoto.isPending

  return (
    <div className="flex items-center gap-1">
      <input
        ref={berkas}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          unggah.mutate(
            { itemId: item.id, file },
            {
              onSuccess: () => toast.success('Foto diperbarui'),
              onError: (error) =>
                toast.error('Foto gagal diunggah', error instanceof Error ? error.message : undefined),
            },
          )
        }}
      />

      <Button
        size="sm"
        variant="ghost"
        disabled={sibuk}
        onClick={() => berkas.current?.click()}
        aria-label={item.image_url ? 'Ganti foto karya' : 'Unggah foto karya'}
      >
        {sibuk ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
      </Button>

      {item.image_url ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={sibuk}
          onClick={() =>
            hapusFoto.mutate(item.id, {
              onSuccess: () => toast.success('Foto dihapus'),
              onError: (error) =>
                toast.error('Gagal menghapus foto', error instanceof ApiError ? error.message : undefined),
            })
          }
          aria-label="Hapus foto karya"
        >
          <ImageMinus className="size-4" />
        </Button>
      ) : null}

      <Button size="sm" variant="ghost" className="text-danger" onClick={onHapus} aria-label="Hapus karya">
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}

export function ProfilePage() {
  const { data: session } = useSession()

  if (!session) return <Skeleton className="h-96 rounded-2xl" />

  const isExpert = session.user.roles.includes('expert')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Profil &amp; keamanan</h1>
        <p className="mt-1 text-muted">Kelola identitas publik dan perangkat yang sedang masuk.</p>
      </header>

      <Card>
        <CardBody className="flex flex-wrap items-center gap-5">
          <AvatarUploader user={session.user} />
          <div className="min-w-0">
            <p className="text-xl font-extrabold text-ink">{session.user.full_name}</p>
            <p className="mt-0.5 text-muted">{session.user.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {session.user.roles.map((role) => (
                <Badge key={role} tone="brand">
                  {roleLabels[role] ?? role}
                </Badge>
              ))}
              {session.user.email_verified ? (
                <Badge tone="success">Email terverifikasi</Badge>
              ) : (
                <Badge tone="warning">Email belum diverifikasi</Badge>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {isExpert ? <ExpertProfileForm userId={session.user.id} /> : null}

      {/* Riwayat kerja dan portofolio dibaca perekrut saat menilai lamaran,
          jadi bagian ini terbuka untuk semua peran — bukan hanya tenaga
          ahli yang menjual jasa. */}
      <KeamananCard user={session.user} />

      <TalentSection userId={session.user.id} />

      <DeviceSessions />
    </div>
  )
}

function ExpertProfileForm({ userId }: { userId: string }) {
  const { data: profile } = useExpert(userId)
  const save = useSaveExpertProfile()
  const toast = useToast()

  const [values, setValues] = useState({
    headline: '',
    bio: '',
    city: '',
    hourly_rate: 0,
    availability: 'open',
  })
  const [skills, setSkills] = useState<string[]>([])
  const [skillDraft, setSkillDraft] = useState('')

  useEffect(() => {
    if (!profile) return
    setValues({
      headline: profile.headline,
      bio: profile.bio,
      city: profile.city,
      hourly_rate: profile.hourly_rate ?? 0,
      availability: profile.availability,
    })
    setSkills(profile.skills)
  }, [profile])

  const addSkill = () => {
    const value = skillDraft.trim().toLowerCase()
    if (!value || skills.includes(value) || skills.length >= 20) return
    setSkills((current) => [...current, value])
    setSkillDraft('')
  }

  return (
    <Card>
      <CardHeader
        title="Profil tenaga ahli"
        description="Profil ini tampil di direktori talenta dan pada proposal yang Anda kirim."
      />
      <CardBody className="space-y-4">
        <Input
          label="Headline"
          value={values.headline}
          onChange={(event) => setValues((current) => ({ ...current, headline: event.target.value }))}
          placeholder="Misalnya: Data Analyst & Pengajar SQL"
        />

        <Textarea
          label="Tentang Anda"
          rows={5}
          value={values.bio}
          onChange={(event) => setValues((current) => ({ ...current, bio: event.target.value }))}
          placeholder="Pengalaman, jenis pekerjaan yang biasa ditangani, dan cara Anda bekerja."
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label="Kota"
            value={values.city}
            onChange={(event) => setValues((current) => ({ ...current, city: event.target.value }))}
          />
          <Input
            label="Tarif per jam"
            type="number"
            min={0}
            step={50000}
            prefix="Rp"
            value={values.hourly_rate || ''}
            onChange={(event) => setValues((current) => ({ ...current, hourly_rate: Number(event.target.value) }))}
          />
          <Select
            label="Ketersediaan"
            value={values.availability}
            onChange={(event) => setValues((current) => ({ ...current, availability: event.target.value }))}
          >
            <option value="open">Menerima proyek</option>
            <option value="limited">Terbatas</option>
            <option value="closed">Sedang tidak menerima</option>
          </Select>
        </div>

        <div>
          <label htmlFor="profile-skill" className="text-sm font-semibold text-ink">
            Keahlian
          </label>
          <div className="mt-1.5 flex gap-2">
            <Input
              id="profile-skill"
              value={skillDraft}
              onChange={(event) => setSkillDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addSkill()
                }
              }}
              placeholder="sql, figma, react…"
            />
            <Button type="button" variant="outline" onClick={addSkill}>
              Tambah
            </Button>
          </div>

          {skills.length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {skills.map((skill) => (
                <li key={skill}>
                  <button
                    type="button"
                    onClick={() => setSkills((current) => current.filter((item) => item !== skill))}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1 text-sm font-medium text-brand-ink"
                  >
                    {skill}
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  headline: values.headline,
                  bio: values.bio,
                  skills,
                  city: values.city,
                  hourly_rate: values.hourly_rate > 0 ? values.hourly_rate : undefined,
                  availability: values.availability,
                },
                {
                  onSuccess: () => toast.success('Profil tersimpan'),
                  onError: (error) =>
                    toast.error('Gagal menyimpan profil', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Simpan profil
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

function DeviceSessions() {
  const { data, isLoading } = useDeviceSessions()
  const revoke = useRevokeSession()
  const toast = useToast()

  return (
    <Card>
      <CardHeader
        title="Perangkat aktif"
        description="Cabut akses perangkat yang tidak Anda kenali. Pencabutan berlaku seketika."
      />

      {isLoading ? (
        <CardBody>
          <Skeleton className="h-20" />
        </CardBody>
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {data?.sessions.map((deviceSession) => (
            <li key={deviceSession.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sunken text-muted">
                  <Laptop className="size-5" />
                </span>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    {deviceSession.current ? 'Perangkat ini' : 'Perangkat lain'}
                    {deviceSession.current ? <Badge tone="success">Aktif</Badge> : null}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted">
                    {deviceSession.ip ?? 'IP tidak tercatat'} · masuk {formatRelative(deviceSession.created_at)}
                  </p>
                  <p className="text-xs text-subtle">Berakhir {formatDateTime(deviceSession.expires_at)}</p>
                </div>
              </div>

              {!deviceSession.current ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger"
                  loading={revoke.isPending}
                  onClick={() =>
                    revoke.mutate(deviceSession.id, {
                      onSuccess: () => toast.success('Sesi dicabut'),
                      onError: () => toast.error('Gagal mencabut sesi'),
                    })
                  }
                >
                  <LogOut className="size-4" />
                  Cabut
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <CardBody className="flex items-start gap-3 border-t border-line text-sm text-muted">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" />
        <p>
          Sesi memakai token acak yang disimpan di server, bukan token mandiri di peramban. Karena itu
          pencabutan berlaku detik itu juga, tanpa menunggu masa berlaku habis.
        </p>
      </CardBody>
    </Card>
  )
}

function TalentSection({ userId }: { userId: string }) {
  const { data: profile } = useTalentProfile(userId)
  const saveDetails = useSaveTalentDetails()
  const addExperience = useAddExperience()
  const addEducation = useAddEducation()
  const addPortfolio = useAddPortfolio()
  const deleteExperience = useDeleteTalentItem('experiences')
  const deleteEducation = useDeleteTalentItem('educations')
  const deletePortfolio = useDeleteTalentItem('portfolios')
  const toast = useToast()

  const [details, setDetails] = useState({
    years_experience: 0,
    resume_url: '',
    portfolio_url: '',
    open_to_work: false,
    preferred_employment: [] as string[],
  })
  const [dialog, setDialog] = useState<'experience' | 'education' | 'portfolio' | null>(null)

  useEffect(() => {
    if (!profile) return
    setDetails({
      years_experience: profile.years_experience,
      resume_url: profile.resume_url ?? '',
      portfolio_url: profile.portfolio_url ?? '',
      open_to_work: profile.open_to_work,
      preferred_employment: profile.preferred_employment,
    })
  }, [profile])

  const toggleEmployment = (value: string) => {
    setDetails((current) => ({
      ...current,
      preferred_employment: current.preferred_employment.includes(value)
        ? current.preferred_employment.filter((item) => item !== value)
        : [...current.preferred_employment, value],
    }))
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Kesiapan bekerja"
          description="Dibaca perekrut saat menilai lamaran Anda dan tampil pada direktori talenta."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Total pengalaman (tahun)"
              type="number"
              min={0}
              max={70}
              value={details.years_experience}
              onChange={(event) =>
                setDetails((current) => ({ ...current, years_experience: Number(event.target.value) }))
              }
            />
            <Input
              label="Tautan CV"
              placeholder="https://…"
              value={details.resume_url}
              onChange={(event) => setDetails((current) => ({ ...current, resume_url: event.target.value }))}
            />
            <Input
              label="Tautan portofolio"
              placeholder="https://…"
              value={details.portfolio_url}
              onChange={(event) => setDetails((current) => ({ ...current, portfolio_url: event.target.value }))}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-3.5">
            <input
              type="checkbox"
              checked={details.open_to_work}
              onChange={(event) => setDetails((current) => ({ ...current, open_to_work: event.target.checked }))}
              className="mt-0.5 size-4 accent-[var(--brand)]"
            />
            <span>
              <span className="block text-sm font-semibold text-ink">Terbuka untuk peluang kerja</span>
              <span className="mt-0.5 block text-sm text-muted">
                Menandai diri terbuka membantu perekrut menemukan Anda; status ini terpisah dari kesediaan
                menerima proyek lepas.
              </span>
            </span>
          </label>

          <fieldset>
            <legend className="text-sm font-semibold text-ink">Jenis pekerjaan yang diminati</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {['full_time', 'part_time', 'contract', 'internship', 'freelance'].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleEmployment(value)}
                  aria-pressed={details.preferred_employment.includes(value)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
                    details.preferred_employment.includes(value)
                      ? 'border-brand bg-brand-soft text-brand-ink'
                      : 'border-line text-muted hover:text-ink'
                  }`}
                >
                  {employmentLabel[value]}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end">
            <Button
              loading={saveDetails.isPending}
              onClick={() =>
                saveDetails.mutate(
                  {
                    years_experience: details.years_experience,
                    resume_url: details.resume_url || undefined,
                    portfolio_url: details.portfolio_url || undefined,
                    open_to_work: details.open_to_work,
                    preferred_employment: details.preferred_employment,
                  },
                  {
                    onSuccess: () => toast.success('Kesiapan bekerja tersimpan'),
                    onError: (error) =>
                      toast.error('Gagal menyimpan', error instanceof ApiError ? error.message : undefined),
                  },
                )
              }
            >
              Simpan
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Pengalaman kerja"
          action={
            <Button size="sm" variant="outline" onClick={() => setDialog('experience')}>
              Tambah
            </Button>
          }
        />
        {profile?.experiences.length ? (
          <ul className="divide-y divide-[var(--border)]">
            {profile.experiences.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{item.role_title}</p>
                  <p className="text-sm text-muted">{item.company_name}</p>
                  <p className="mt-0.5 text-xs text-subtle">
                    {formatDateShort(item.started_on)} —{' '}
                    {item.is_current ? 'sekarang' : item.ended_on ? formatDateShort(item.ended_on) : 'selesai'}
                  </p>
                  {item.description ? <p className="mt-2 text-sm text-muted">{item.description}</p> : null}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger"
                  onClick={() => deleteExperience.mutate(item.id)}
                >
                  Hapus
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <CardBody>
            <p className="text-sm text-muted">Belum ada pengalaman kerja yang dicantumkan.</p>
          </CardBody>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Pendidikan"
          action={
            <Button size="sm" variant="outline" onClick={() => setDialog('education')}>
              Tambah
            </Button>
          }
        />
        {profile?.educations.length ? (
          <ul className="divide-y divide-[var(--border)]">
            {profile.educations.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{item.institution}</p>
                  <p className="text-sm text-muted">
                    {[item.degree, item.field].filter(Boolean).join(' · ') || 'Program tidak disebutkan'}
                  </p>
                  {item.start_year || item.end_year ? (
                    <p className="mt-0.5 text-xs text-subtle">
                      {item.start_year ?? '—'} – {item.end_year ?? 'sekarang'}
                    </p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger"
                  onClick={() => deleteEducation.mutate(item.id)}
                >
                  Hapus
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <CardBody>
            <p className="text-sm text-muted">Belum ada riwayat pendidikan yang dicantumkan.</p>
          </CardBody>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Portofolio"
          description="Karya yang dapat dilihat calon klien dan perekrut."
          action={
            <Button size="sm" variant="outline" onClick={() => setDialog('portfolio')}>
              Tambah
            </Button>
          }
        />
        {profile?.portfolios.length ? (
          <CardBody>
            <PortfolioCarousel
              items={profile.portfolios}
              renderAction={(item) => (
                <AksiKarya
                  item={item}
                  onHapus={() => deletePortfolio.mutate(item.id)}
                />
              )}
            />
          </CardBody>
        ) : (
          <CardBody>
            <p className="text-sm text-muted">
              Belum ada karya pada portofolio. Tambahkan karya beserta fotonya — inilah yang paling
              sering dilihat calon klien sebelum menghubungi Anda.
            </p>
          </CardBody>
        )}
      </Card>

      <TalentItemDialog
        kind={dialog}
        onClose={() => setDialog(null)}
        onSubmitExperience={(values) =>
          addExperience.mutate(values, {
            onSuccess: () => {
              toast.success('Pengalaman ditambahkan')
              setDialog(null)
            },
            onError: (error) => toast.error('Gagal menambahkan', error instanceof ApiError ? error.message : undefined),
          })
        }
        onSubmitEducation={(values) =>
          addEducation.mutate(values, {
            onSuccess: () => {
              toast.success('Pendidikan ditambahkan')
              setDialog(null)
            },
            onError: (error) => toast.error('Gagal menambahkan', error instanceof ApiError ? error.message : undefined),
          })
        }
        onSubmitPortfolio={(values) =>
          addPortfolio.mutate(values, {
            onSuccess: () => {
              toast.success('Karya ditambahkan')
              setDialog(null)
            },
            onError: (error) => toast.error('Gagal menambahkan', error instanceof ApiError ? error.message : undefined),
          })
        }
      />
    </>
  )
}

function TalentItemDialog({
  kind,
  onClose,
  onSubmitExperience,
  onSubmitEducation,
  onSubmitPortfolio,
}: {
  kind: 'experience' | 'education' | 'portfolio' | null
  onClose: () => void
  onSubmitExperience: (values: {
    company_name: string
    role_title: string
    started_on: string
    ended_on?: string
    is_current: boolean
    description?: string
  }) => void
  onSubmitEducation: (values: {
    institution: string
    degree?: string
    field?: string
    start_year?: number
    end_year?: number
  }) => void
  onSubmitPortfolio: (values: { title: string; description?: string; url?: string; image_url?: string }) => void
}) {
  const [experience, setExperience] = useState({
    company_name: '',
    role_title: '',
    started_on: '',
    ended_on: '',
    is_current: false,
    description: '',
  })
  const [education, setEducation] = useState({ institution: '', degree: '', field: '', start_year: '', end_year: '' })
  const [portfolio, setPortfolio] = useState({ title: '', description: '', url: '', image_url: '' })

  const titles = {
    experience: 'Tambah pengalaman kerja',
    education: 'Tambah riwayat pendidikan',
    portfolio: 'Tambah karya portofolio',
  }

  return (
    <Dialog
      open={kind !== null}
      onClose={onClose}
      size="sm"
      title={kind ? titles[kind] : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            onClick={() => {
              if (kind === 'experience') {
                onSubmitExperience({
                  company_name: experience.company_name,
                  role_title: experience.role_title,
                  started_on: experience.started_on,
                  ended_on: experience.is_current || !experience.ended_on ? undefined : experience.ended_on,
                  is_current: experience.is_current,
                  description: experience.description || undefined,
                })
              }
              if (kind === 'education') {
                onSubmitEducation({
                  institution: education.institution,
                  degree: education.degree || undefined,
                  field: education.field || undefined,
                  start_year: education.start_year ? Number(education.start_year) : undefined,
                  end_year: education.end_year ? Number(education.end_year) : undefined,
                })
              }
              if (kind === 'portfolio') {
                onSubmitPortfolio({
                  title: portfolio.title,
                  description: portfolio.description || undefined,
                  url: portfolio.url || undefined,
                  image_url: portfolio.image_url || undefined,
                })
              }
            }}
          >
            Simpan
          </Button>
        </>
      }
    >
      {kind === 'experience' ? (
        <div className="space-y-4">
          <Input
            label="Posisi"
            value={experience.role_title}
            onChange={(event) => setExperience((current) => ({ ...current, role_title: event.target.value }))}
          />
          <Input
            label="Perusahaan"
            value={experience.company_name}
            onChange={(event) => setExperience((current) => ({ ...current, company_name: event.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Mulai"
              type="date"
              value={experience.started_on}
              onChange={(event) => setExperience((current) => ({ ...current, started_on: event.target.value }))}
            />
            <Input
              label="Selesai"
              type="date"
              disabled={experience.is_current}
              value={experience.ended_on}
              onChange={(event) => setExperience((current) => ({ ...current, ended_on: event.target.value }))}
            />
          </div>
          <Checkbox
            label="Masih bekerja di sini"
            checked={experience.is_current}
            onChange={(event) => setExperience((current) => ({ ...current, is_current: event.target.checked }))}
          />
          <Textarea
            label="Ringkasan pekerjaan"
            rows={3}
            value={experience.description}
            onChange={(event) => setExperience((current) => ({ ...current, description: event.target.value }))}
          />
        </div>
      ) : null}

      {kind === 'education' ? (
        <div className="space-y-4">
          <Input
            label="Institusi"
            value={education.institution}
            onChange={(event) => setEducation((current) => ({ ...current, institution: event.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Jenjang"
              placeholder="S1, D3, SMK…"
              value={education.degree}
              onChange={(event) => setEducation((current) => ({ ...current, degree: event.target.value }))}
            />
            <Input
              label="Bidang"
              value={education.field}
              onChange={(event) => setEducation((current) => ({ ...current, field: event.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Tahun mulai"
              type="number"
              min={1950}
              max={2100}
              value={education.start_year}
              onChange={(event) => setEducation((current) => ({ ...current, start_year: event.target.value }))}
            />
            <Input
              label="Tahun selesai"
              type="number"
              min={1950}
              max={2100}
              value={education.end_year}
              onChange={(event) => setEducation((current) => ({ ...current, end_year: event.target.value }))}
            />
          </div>
        </div>
      ) : null}

      {kind === 'portfolio' ? (
        <div className="space-y-4">
          <Input
            label="Judul karya"
            value={portfolio.title}
            onChange={(event) => setPortfolio((current) => ({ ...current, title: event.target.value }))}
          />
          <Textarea
            label="Keterangan"
            rows={3}
            value={portfolio.description}
            onChange={(event) => setPortfolio((current) => ({ ...current, description: event.target.value }))}
          />
          <Input
            label="Tautan karya"
            placeholder="https://…"
            value={portfolio.url}
            onChange={(event) => setPortfolio((current) => ({ ...current, url: event.target.value }))}
          />
          <Input
            label="URL gambar"
            placeholder="https://…"
            value={portfolio.image_url}
            onChange={(event) => setPortfolio((current) => ({ ...current, image_url: event.target.value }))}
          />
        </div>
      ) : null}
    </Dialog>
  )
}

/**
 * AvatarUploader menampilkan foto profil beserta kendali menggantinya.
 *
 * Berkas dipilih lewat input file tersembunyi yang dipicu tombol, supaya
 * tampilannya seragam dengan tombol lain — kendali file bawaan peramban
 * tampil berbeda di setiap sistem dan tidak dapat ditata.
 */
function AvatarUploader({ user }: { user: User }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadAvatar()
  const remove = useRemoveAvatar()
  const toast = useToast()

  const pilih = (file?: File) => {
    if (!file) return

    // Ukuran diperiksa sebelum dikirim: pada jaringan seluler, menunggu
    // unggahan tiga megabita hanya untuk ditolak peladen adalah pemborosan
    // yang dapat dihindari.
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Foto terlalu besar', 'Ukuran maksimal 2 MB. Perkecil dulu lalu coba lagi.')
      return
    }

    upload.mutate(file, {
      onSuccess: () => toast.success('Foto profil diperbarui'),
      onError: (error) =>
        toast.error('Gagal mengunggah foto', error instanceof ApiError ? error.message : undefined),
    })
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar name={user.full_name} src={user.avatar_url} size="xl" />
        {upload.isPending ? (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-inverted/60">
            <Loader2 className="size-6 animate-spin text-on-inverted" />
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          onChange={(event) => {
            pilih(event.target.files?.[0])
            // Nilai dikosongkan supaya memilih berkas yang sama dua kali
            // tetap memicu perubahan.
            event.target.value = ''
          }}
        />

        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} loading={upload.isPending}>
          <Camera className="size-4" />
          {user.avatar_url ? 'Ganti foto' : 'Unggah foto'}
        </Button>

        {user.avatar_url ? (
          <Button
            size="sm"
            variant="ghost"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(undefined, {
                onSuccess: () => toast.success('Foto profil dihapus'),
                onError: () => toast.error('Gagal menghapus foto'),
              })
            }
          >
            Hapus foto
          </Button>
        ) : (
          <p className="text-xs text-subtle">JPG, PNG, atau WebP · maks 2 MB</p>
        )}
      </div>
    </div>
  )
}

/**
 * KeamananCard menyatukan verifikasi surel dan penggantian kata sandi.
 *
 * Keduanya diletakkan berdampingan karena keduanya menjawab pertanyaan yang
 * sama bagi pengguna: seberapa aman akun saya, dan apa yang perlu saya
 * lakukan sekarang.
 */
function KeamananCard({ user }: { user: User }) {
  const kirimVerifikasi = useSendVerification()
  const gantiSandi = useChangePassword()
  const toast = useToast()

  const [nilai, setNilai] = useState({ current_password: '', new_password: '', confirm: '' })

  const sandiTidakCocok =
    nilai.confirm.length > 0 && nilai.new_password !== nilai.confirm
      ? 'Kedua kata sandi belum sama.'
      : undefined

  const siapKirim =
    nilai.current_password.length > 0 && nilai.new_password.length >= 8 && !sandiTidakCocok

  return (
    <Card>
      <CardHeader title="Keamanan akun" description="Verifikasi surel dan kata sandi." />
      <CardBody className="space-y-6">
        {!user.email_verified ? (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-warning/40 bg-warning-soft p-4">
            <div className="min-w-0">
              <p className="font-bold text-ink">Alamat surel belum diverifikasi</p>
              <p className="mt-0.5 text-sm text-muted">
                Verifikasi dibutuhkan sebelum transaksi pertama Anda, baik membeli kelas maupun mencairkan dana.
              </p>
            </div>
            <Button
              size="sm"
              loading={kirimVerifikasi.isPending}
              onClick={() =>
                kirimVerifikasi.mutate(undefined, {
                  onSuccess: () =>
                    toast.success('Tautan verifikasi terkirim', 'Periksa kotak masuk surel Anda.'),
                  onError: (error) =>
                    toast.error(
                      'Gagal mengirim tautan',
                      error instanceof ApiError ? error.message : undefined,
                    ),
                })
              }
            >
              Kirim tautan verifikasi
            </Button>
          </div>
        ) : null}

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            gantiSandi.mutate(
              { current_password: nilai.current_password, new_password: nilai.new_password },
              {
                onSuccess: () => {
                  setNilai({ current_password: '', new_password: '', confirm: '' })
                  toast.success('Kata sandi diganti', 'Seluruh perangkat dikeluarkan. Masuk kembali.')
                  // Seluruh sesi dicabut peladen, jadi halaman dimuat ulang
                  // dari nol agar pengguna mendarat di halaman masuk.
                  window.location.assign('/masuk')
                },
                onError: (error) =>
                  toast.error(
                    'Gagal mengganti kata sandi',
                    error instanceof ApiError ? error.message : undefined,
                  ),
              },
            )
          }}
        >
          <p className="font-bold text-ink">Ganti kata sandi</p>

          <Input
            label="Kata sandi saat ini"
            type="password"
            autoComplete="current-password"
            value={nilai.current_password}
            onChange={(event) => setNilai((v) => ({ ...v, current_password: event.target.value }))}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Kata sandi baru"
              type="password"
              autoComplete="new-password"
              placeholder="Minimal 8 karakter"
              value={nilai.new_password}
              onChange={(event) => setNilai((v) => ({ ...v, new_password: event.target.value }))}
            />
            <Input
              label="Ulangi kata sandi baru"
              type="password"
              autoComplete="new-password"
              value={nilai.confirm}
              onChange={(event) => setNilai((v) => ({ ...v, confirm: event.target.value }))}
              error={sandiTidakCocok}
            />
          </div>

          <p className="text-sm text-subtle">
            Seluruh perangkat yang sedang masuk akan dikeluarkan setelah kata sandi berganti.
          </p>

          <Button type="submit" disabled={!siapKirim} loading={gantiSandi.isPending}>
            Simpan kata sandi baru
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}
